import { __rest } from 'tslib';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import React__default, { useCallback, useRef, forwardRef, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import MuiBox from '@mui/material/Box';
import { useDrag } from 'react-dnd';
import Checkbox from '../../../Checkbox/Checkbox.js';
import '../../../Checkbox/CheckboxList.js';
import SpriteIcon from '../../../SpriteIcon/SpriteIcon.js';
import Hint from '../../../Hint/Hint.js';

const Root = styled(MuiBox, {
    shouldForwardProp: prop => !['rtl'].includes(prop),
}) `
  ${({ theme, rtl }) => `
    display: flex;
    position: relative;
    border-radius: 4px;
    display: flex;
    justify-content: center;
    width: 100%;
    position: relative;
    top: ${theme.spacing(-0.25)};
    &.ops-row-selection-cell {
      .ops-row-selection-icon,
      .ops-row-selection-actions,
      .ops-row-selection-drag {
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        transition: opacity 0.125s ease-in-out;
      }
      .ops-row-selection-checkbox {
        padding: 20px 5px;
      }
      .safeguard {
        position: absolute;
        pointer-events: none;
      }
      .ops-row-selection-drag {
        left: ${rtl ? 'auto' : theme.spacing(-1)};
        right: ${rtl ? theme.spacing(-2.5) : 'auto'};
        cursor: grab;
        margin-top: ${theme.spacing(0.15)};
      }
    }
  `}
`;
const DragDummyPreview = styled('span') `
  color: transparent;
  opacity: 0;
`;
const CheckboxCellRenderer = React__default.memo(({ Icon, iconName, row, checked, onChange, onRowCheckboxChanged, selectedRowIds, enableRowDragging, rtl, }) => {
    const onCheckboxChange = useCallback((e, checked) => {
        row.markRowRange(e, checked);
        onChange && onChange(e);
        onRowCheckboxChanged && onRowCheckboxChanged(row, checked);
    }, [onChange, onRowCheckboxChanged, row]);
    const previewRef = useRef(null);
    const [{ isDragging }, drag, preview] = useDrag(() => ({
        type: 'row',
        canDrag: enableRowDragging || false,
        item: {
            id: row.id,
            checked,
            selectedRowIds,
        },
        collect: monitor => ({
            isDragging: monitor.isDragging(),
        }),
    }), [selectedRowIds, checked, row.id]);
    preview(previewRef);
    const selectedRowIdsCount = Object.keys(selectedRowIds).length;
    return (jsxs(Root, Object.assign({ className: "ops-row-selection-cell", rtl: rtl }, { children: [jsx(DragDummyPreview, Object.assign({ ref: previewRef }, { children: "dummy" })), jsxs(Fragment, { children: [Icon && !iconName && jsx(Icon, { className: "ops-row-selection-icon" }), iconName && !Icon && jsx(SpriteIcon, { className: "ops-row-selection-icon", name: iconName })] }), jsxs("div", Object.assign({ className: "ops-row-selection-actions", ref: drag }, { children: [enableRowDragging && (jsx(Hint, Object.assign({ title: !isDragging ? `Move ${selectedRowIdsCount || 1} item${selectedRowIdsCount > 1 ? 's' : ''}` : '', "data-testid": `${row.original.id}-drag-handler` }, { children: jsx("div", Object.assign({ className: "ops-row-selection-drag" }, { children: jsx(SpriteIcon, { name: "EllipsisVSmall" }) })) }))), jsx("div", Object.assign({ className: "ops-row-selection-checkbox", onClick: e => e.stopPropagation() }, { children: jsx(Checkbox, { "data-testid": `${row.id}-selection-checkbox`, checked: checked, onChange: onCheckboxChange, rtl: rtl }) })), jsx("div", { className: "safeguard", onClick: e => e.stopPropagation() })] }))] })));
});
CheckboxCellRenderer.displayName = 'CheckboxCellRenderer';
const CheckboxHeaderCellRenderer = forwardRef((_a, _) => {
    var { indeterminate, onSelectAllCheckboxChanged, onChange, title } = _a, rest = __rest(_a, ["indeterminate", "onSelectAllCheckboxChanged", "onChange", "title"]);
    const defaultRef = useRef(null);
    useEffect(() => {
        if (defaultRef.current) {
            defaultRef.current.indeterminate = true;
        }
    }, [indeterminate, defaultRef]);
    const onCheckboxChange = useCallback((e) => {
        onChange && onChange(e);
        onSelectAllCheckboxChanged && onSelectAllCheckboxChanged(e.target.checked);
    }, [onSelectAllCheckboxChanged, onChange]);
    return (jsx(Root, { children: jsx(Checkbox, Object.assign({}, rest, { hint: title, inputRef: defaultRef, selectAll: true, semi: indeterminate, onChange: onCheckboxChange })) }));
});
CheckboxHeaderCellRenderer.displayName = 'CheckboxHeaderCellRenderer';

export { CheckboxCellRenderer, CheckboxHeaderCellRenderer };
//# sourceMappingURL=CheckboxCellRenderer.js.map
